import { FC, useCallback, useEffect, useState } from 'react';
import {
    ButtonSizes,
    ButtonStyles,
    DefaultFooterComponent,
    Popup,
    ProcessButton,
    ProcessButtonStatus
} from '@silverfort/sf-components';
import UserSelectList from './UserSelectList';
import { Entity } from '../../../../../api/types/Entity';
import useTestMFAToken, { TestMFAResponseData, TestMFATokenPayloadData } from '../api/mutations/useTestMFAToken';

interface Props {
    open: boolean;
    onConfirm: VoidFunction;
    onCancel: VoidFunction;
    testFactor: string;
    testFactorTenantId?: string;
}

type ContentComponentProps = Pick<Props, 'testFactor' | 'testFactorTenantId'> & {
    selectedUser?: Entity;
    setSelectedUser: React.Dispatch<React.SetStateAction<Entity | undefined>>;
    testStatus: ProcessButtonStatus;
    setTestStatus: React.Dispatch<React.SetStateAction<ProcessButtonStatus>>;
};
const ContentComponent: FC<ContentComponentProps> = ({
    testFactorTenantId,
    testFactor,
    selectedUser,
    setSelectedUser,
    testStatus,
    setTestStatus
}) => {
    const [testResponseMessage, setTestResponseMessage] = useState<string>();

    const onSuccessCallback = useCallback(
        (data: TestMFAResponseData) => {
            if (!data.sentSuccessfully) {
                setTestStatus((prevState) => {
                    if (prevState === 'loading') {
                        return 'failure';
                    }
                    return prevState;
                });

                setTestResponseMessage(data?.responseText);
            }

            setTestStatus((prevState) => {
                if (prevState === 'loading') {
                    return 'success';
                }
                return prevState;
            });
        },
        [setTestStatus]
    );

    const onErrorCallback = useCallback(() => {
        setTestStatus((prevState) => {
            if (prevState === 'loading') {
                return 'failure';
            }
            return prevState;
        });
    }, [setTestStatus]);

    const { testMFATokenMutation } = useTestMFAToken({
        onSuccess: onSuccessCallback,
        onError: onErrorCallback
    });

    const handleTest = useCallback(() => {
        if (!selectedUser) {
            return;
        }

        const requestData: TestMFATokenPayloadData = {
            username: selectedUser.sam || selectedUser.user_name,
            domainName: selectedUser.domainName
        };

        if (testFactor) {
            requestData.authenticator = testFactor;
        }

        const providerId = testFactorTenantId || testFactor;

        if (providerId) {
            requestData.provider_ids = [providerId];
            requestData.tokenId = testFactorTenantId;
            requestData.tokenType = null;
            requestData.tokenId = providerId;
            requestData.device = null;
        }

        setTestStatus('loading');

        testMFATokenMutation(requestData);
    }, [selectedUser, setTestStatus, testFactor, testFactorTenantId, testMFATokenMutation]);

    const handleCancelTest = useCallback(() => {
        setTestStatus('initial');
    }, [setTestStatus]);

    useEffect(() => {
        return () => {
            setTestStatus('initial');
            setTestResponseMessage(undefined);
            setSelectedUser(undefined);
        };
    }, []);

    return (
        <div className="flex flex-row flex-1 justify-between items-center font-sf gap-8">
            <div
                className={`flex flex-row items-center justify-between font-medium ${
                    selectedUser ? 'gap-8' : 'gap-16'
                }`}>
                <span className="w-[68px]">User name</span>
                <UserSelectList user={selectedUser} setUser={setSelectedUser} />
            </div>
            <ProcessButton
                status={testStatus}
                buttonStyle={ButtonStyles.TertiarySolid}
                onCancel={handleCancelTest}
                onClick={handleTest}
                disabled={!selectedUser}
                containerSize={ButtonSizes.Small}
                text="Test"
                loadingMessage="Testing token"
                successMessage="Response received"
                failureMessage="Response failed"
                failureInfo={testResponseMessage}
                onRetry={handleTest}
            />
        </div>
    );
};

const TestMFATokenPopup: FC<Props> = ({ open, testFactor, testFactorTenantId, onConfirm, onCancel }) => {
    const [selectedUser, setSelectedUser] = useState<Entity>();
    const [testStatus, setTestStatus] = useState<ProcessButtonStatus>('initial');

    return (
        <Popup
            open={open}
            title="Test token"
            ContentComponent={ContentComponent}
            ContentComponentProps={{
                testFactor,
                testFactorTenantId,
                selectedUser,
                setSelectedUser,
                testStatus,
                setTestStatus
            }}
            FooterComponent={DefaultFooterComponent}
            FooterComponentProps={{
                primaryButtonProps: { text: 'Done', disabled: testStatus === 'loading' }
            }}
            onClose={onCancel}
            popupClassName="!w-[692px] !max-w-[692px] h-[166px]"
            contentClassName="overflow-x-hidden"
        />
    );
};
export default TestMFATokenPopup;
